import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import { getCountryCallingCode } from 'react-phone-number-input';
import {
  CardInitialState,
  SearchStatus,
  BookingAction,
  PassengerInfoFormValidation,
  TravelTypes,
  GetPassengerTypes,
  CreditCardsFormValidation,
  PaymentMethodInitialState,
} from '../../constants';
import AirPreferencesMealTypeCodes from '../../constants/AirPreferencesMealTypeCodes';
import FlightSpecialAssistanceCodes from '../../constants/FlightSpecialAssistanceCodes';
import FlightSeatPreferences from '../../constants/FlightSeatPreferences';
import formatCruisePayments from '../../helpers/formatCruisePayments';
import formatTrip from '../../helpers/formatTrip';
import calculateAge from '../../helpers/calculateAge';
import getTripItineraries from '../../helpers/getTripItineraries';

const initialState = {
  selectedTrips: {},
  chargeableItemOptions: [],
  paymentMethods: {
    0: {
      ...PaymentMethodInitialState,
    },
  },
  passengers: {},
  error: null,
  priceCheckError: null,
  saveSendTripEmailError: null,
  tripConfirmation: null,
  bookingStatus: null,
  bookingAction: BookingAction.BOOK,
  status: SearchStatus.INITIAL,
  tripBookingStatus: SearchStatus.INITIAL,
  updateTripStatus: SearchStatus.INITIAL,
  saveSendTripEmailStatus: SearchStatus.INITIAL,
  getDiningOptionsStatus: SearchStatus.INITIAL,
  getBasketContentStatuses: {},
  similarPassengersValidation: null,
  isAgencyPlanningFeesValid: true,
  specialBookingRequests: {},
  hotelChainAdvisorBySegment: {},
  doSaveHotelChainAdvisorBySegment: {},
  noteAirDeskRequest: null,
  rateOptionRules: {},
  isTripAvailabilityPriceChanged: false,
  tripAvailabilityPriceChangeResponse: null,
  clientOverwriteWarning: null,
  tripPriceCheckStatus: SearchStatus.INITIAL,
  acceptTripPriceChangeStatus: SearchStatus.INITIAL,
  isBookedTripPolicyReviewValidated: true,
  hasReviewedBookedTripPolicy: false,
  isBookingTripPolicyReviewValidated: true,
  hasReviewedBookingTripPolicy: false,
  invalidAdditionalCheckoutFields: {},
  isBookingReviewManual: false,
  isNoteAirDeskValidated: true,
  isAgreedForDataCollection: true,
  hasAgreedForDataCollection: false,
  getPackagesStatus: SearchStatus.INITIAL,
  getPackageError: null,
  getSpecialServicesStatus: SearchStatus.INITIAL,
  getSpecialServicesError: null,
  tripsSearch: {
    searchObj: {
      areFiltersEmpty: true,
      selectedUsers: [],
      sortBy: 0,
      sortDirection: 1,
      doViewAll: true,
      bookingStatus: [],
      fromTripBookingDate: undefined,
      toTripBookingDate: undefined,
      depositDue: [],
      finalPaymentDue: [],
      page: 1,
      tripStartDateFrom: undefined,
      tripStartDateTo: undefined,
    },
    filterObj: { selectedUsers: [], allUsers: [], totalTrips: null },
    trips: null,
  },
  searchTripsStatus: SearchStatus.INITIAL,
  cancelBookingStatus: SearchStatus.INITIAL,
  cancelBookingError: null,
  cancelBookingWasVoided: false,
  tripsCsv: null,
  deleteTripStatus: {},
  agencyPlanningFees: 0,
  doSplitServiceFees: false,
  agencyPlanningFeesKeptByAgency: 0,
  doChargeAgencyPlanningFees: false,
  // inputs the planning fee defaults were last seeded from (see TravelAgencyPlanningFee);
  // stops a checkout remount (browser back/forward, etc) from overwriting an entered fee.
  agencyPlanningFeesSeedKey: null,
  sabrePnrPrintout: undefined,
  sabreCommandsStatus: SearchStatus.INITIAL,
  ignoreCachedResultsForModes: undefined,
  getTripsCsvStatus: SearchStatus.INITIAL,
  customerIdentifier: '',
  promoCode: '',
  requestorIATA: undefined,
  travelAgencyAdvisorsEmail: '',
  isSelfBookingAllowed: false,
  userRemarks: [],
  isUserRemarksValidated: true,
  selectedAdditionalCheckoutFields: {},
  markups: {},
  invalidHotelSpecialRequestsBySegmentId: {},
  addCustomBookingFieldStatus: SearchStatus.INITIAL,
  addCustomBookingFieldError: null,
};

const formatPassengers = (tripConfirmation, isUpdatingBookedTrip = false, travelAgency = {}) => {
  if (
    !tripConfirmation ||
    !tripConfirmation.travelers ||
    !Array.isArray(tripConfirmation.travelers)
  ) {
    return [];
  }
  const passengerTypes = GetPassengerTypes(travelAgency && travelAgency.enabledTransportModes);
  const firstFlightSegment = tripConfirmation.trip?.tripSegments
    ?.flatMap((s) => s.itinerary?.flightSegments || [])
    ?.find((fs) => fs.flights?.length);
  const departDateTime = firstFlightSegment?.flights?.[0]?.departDateTime;
  return _.map(tripConfirmation.travelers, (t, passengerIndex) => {
    const age = calculateAge(t.birthDate, departDateTime);
    let passengerType = 'adults';
    if (
      !t.birthDate ||
      (age >= passengerTypes.adults.fromAge &&
        (!passengerTypes.adults.toAge || age <= passengerTypes.adults.toAge))
    )
      passengerType = 'adults';
    else if (passengerTypes.seniors && age >= passengerTypes.seniors.fromAge)
      passengerType = 'seniors';
    else if (
      passengerTypes.children &&
      age >= passengerTypes.children.fromAge &&
      age <= passengerTypes.children.toAge
    )
      passengerType = 'children';
    else if (passengerTypes.lapInfants && age <= passengerTypes.lapInfants.toAge && t.isLapInfant)
      passengerType = 'lapInfants';
    else if (
      passengerTypes.seatInfants &&
      age <= passengerTypes.seatInfants.toAge &&
      !t.isLapInfant
    )
      passengerType = 'seatInfants';

    return {
      ...t,
      // Include country code to phone number if not included
      phone: t.phone
        ? (t.phone[0] !== '+' ? `+${getCountryCallingCode(t.countryCode)}` : '') + t.phone
        : undefined,
      travelerPreferences: t.travelerPreferences
        ? {
            ...t.travelerPreferences,
            frequentFlyerNumbers:
              t.travelerPreferences.frequentFlyerNumbers &&
              !_.isEmpty(t.travelerPreferences.frequentFlyerNumbers)
                ? _.map(t.travelerPreferences.frequentFlyerNumbers, (value, key) => ({
                    selectedValue: { label: key, value: key },
                    value,
                  }))
                : [],
            flightMealPreference: t.travelerPreferences.flightMealPreference
              ? _.find(
                  AirPreferencesMealTypeCodes,
                  (c) => c.value === t.travelerPreferences.flightMealPreference,
                )
              : null,
            flightSeatPreference: t.travelerPreferences.flightSeatPreference
              ? _.find(
                  FlightSeatPreferences,
                  (c) => c.value === t.travelerPreferences.flightSeatPreference,
                )
              : null,
            flightSeatNumbers: !isUpdatingBookedTrip
              ? t.travelerPreferences.flightSeatNumbers
              : undefined,
            knownTravelerNumber: t.travelerPreferences.knownTravelerNumber
              ? t.travelerPreferences.knownTravelerNumber
              : '',
            redressNumber: t.travelerPreferences.redressNumber
              ? t.travelerPreferences.redressNumber
              : '',
            flightSpecialAssistance: t.travelerPreferences.flightSpecialAssistance
              ? _.find(
                  FlightSpecialAssistanceCodes,
                  (c) => c.value === t.travelerPreferences.flightSpecialAssistance,
                )
              : null,
            emergencyContact: t.travelerPreferences.emergencyContact
              ? {
                  ...t.travelerPreferences.emergencyContact,
                  // Include country code to phone number if not included
                  phoneNumber:
                    (t.travelerPreferences.emergencyContact.phoneNumber &&
                    t.travelerPreferences.emergencyContact.phoneNumber[0] !== '+'
                      ? `+${getCountryCallingCode(t.countryCode)}`
                      : '') + t.travelerPreferences.emergencyContact.phoneNumber,
                }
              : undefined,
          }
        : {},
      formValidations: PassengerInfoFormValidation,
      passengerIndex,
      isPastPassenger: t.pastPassengerNumber ? 1 : 0,
      passengerType,
      isPrimaryContact: passengerIndex === 0 ? true : undefined,
    };
  });
};

const tripSlice = createSlice({
  name: 'trip',
  initialState,
  reducers: {
    resetTrips: (state, action) => {
      const doEnableTripConfigDecoupling = action.payload?.doEnableTripConfigDecoupling;
      return {
        ...state,
        selectedTrips: doEnableTripConfigDecoupling
          ? state.selectedTrips
          : initialState.selectedTrips,
        passengers: initialState.passengers,
        paymentMethods: initialState.paymentMethods,
        customerIdentifier: initialState.customerIdentifier,
        promoCode: initialState.promoCode,
        requestorIATA: initialState.requestorIATA,
        travelAgencyAdvisorsEmail: initialState.travelAgencyAdvisorsEmail,
        isAgencyPlanningFeesValid: initialState.isAgencyPlanningFeesValid,
        invalidAdditionalCheckoutFields: initialState.invalidAdditionalCheckoutFields,
        agencyPlanningFeesSeedKey: initialState.agencyPlanningFeesSeedKey,
      };
    },
    addToTrip: (state, action) => {
      const newTrip = {
        [action.payload.tripId]: action.payload.trip
          ? {
              ...state.selectedTrips[action.payload.tripId],
              ...action.payload.trip,
              saveTripStatus: SearchStatus.INITIAL,
              id: action.payload.tripId,
            }
          : null,
      };
      // const areFiltersEmpty = _.isEqual(filters, FiltersInitialValue.cruise.filters);
      return {
        ...state,
        selectedTrips: { ...state.selectedTrips, ...newTrip },
        // setting DONE to make sure trip price check not called when go to results to checkout page
        // trip price check should called when checkout/trip quote page reloaded
        tripPriceCheckStatus: SearchStatus.INITIAL,
      };
    },
    // TODO: refactor both addtotrip and remove - really awkward code, i think can be improved a lot
    removeFromTrip: (state, { payload: { tripId, key, id, selectedRoomIndex } }) => {
      let newTrip = {
        ...state.selectedTrips[tripId],
        saveTripStatus: SearchStatus.INITIAL,
      };
      let doShowMarkupAmountWithItineraryToRemove = false;

      if (selectedRoomIndex !== undefined) {
        const filtered = newTrip[key].cabins.map((i) => {
          if (i && i.roomIndex !== selectedRoomIndex) {
            return i;
          }
          return null;
        });
        newTrip[key] = {
          ...newTrip[key],
          cabins: filtered,
        };
      } else if (id != null && key === 'hotelItineraries' && newTrip.hotelItineraries) {
        const hotelItineraries = Object.values(newTrip.hotelItineraries);
        if (hotelItineraries.length) {
          const hotelItineraryToRemove = hotelItineraries.find((h) => h.id === id);
          if (
            hotelItineraryToRemove &&
            hotelItineraryToRemove.cost &&
            hotelItineraryToRemove.cost.doShowMarkupAmount
          ) {
            doShowMarkupAmountWithItineraryToRemove = true;
          }

          const { selectedRoomCode } = hotelItineraryToRemove;
          const updatedHotelItineraries = hotelItineraries.filter((r) => r.id !== id);

          if (
            updatedHotelItineraries.length === 0 &&
            updatedHotelItineraries.length !== hotelItineraries.length
          ) {
            delete newTrip.selectedRoomCodes;
            delete newTrip.hotelItineraries;
          } else if (updatedHotelItineraries.length !== hotelItineraries.length) {
            newTrip = {
              ...newTrip,
              hotelItineraries: updatedHotelItineraries,
              selectedRoomCodes: newTrip.selectedRoomCodes
                ? newTrip.selectedRoomCodes.filter(
                    (code, i) => i !== newTrip.selectedRoomCodes.indexOf(selectedRoomCode),
                  )
                : [],
            };
          }
        }
      } else if (id != null && newTrip[key]) {
        const matchingItinerary = (
          Array.isArray(newTrip[key]) ? newTrip[key] : Object.values(newTrip[key])
        ).find((f) => f.id === id);
        if (
          matchingItinerary &&
          matchingItinerary.cost &&
          matchingItinerary.cost.doShowMarkupAmount
        ) {
          doShowMarkupAmountWithItineraryToRemove = true;
        }
        let newTripKey = [];
        if (Array.isArray(newTrip[key])) {
          newTripKey = Object.values(newTrip[key]).filter((f) => f.id !== id);
        } else {
          newTripKey = { ...newTrip[key] };
          delete newTripKey[id];
        }

        newTrip[key] = newTripKey;
        if (!Object.keys(newTripKey).length) delete newTrip[key];
      } else {
        if (newTrip[key] && newTrip[key].cost && newTrip[key].cost.doShowMarkupAmount) {
          doShowMarkupAmountWithItineraryToRemove = true;
        }
        delete newTrip[key];
        if (key === 'hotelItineraries') {
          delete newTrip.selectedRoomCodes;
          delete newTrip.hotelItineraries;
        }
      }

      if (!Object.keys(newTrip).length) newTrip = null;
      if (
        newTrip &&
        !newTrip.hotelItineraries &&
        !newTrip.flightItinerary &&
        !newTrip.cruiseItinerary &&
        !newTrip.rentalCarItineraries &&
        !(newTrip.tourItineraries || []).length
      ) {
        newTrip = null;
      }

      if (doShowMarkupAmountWithItineraryToRemove) {
        const itineraries = getTripItineraries(newTrip);
        if (itineraries && itineraries.length) {
          const { mode } = itineraries[0];
          if (mode === TravelTypes.hotel && Object.keys(newTrip.hotelItineraries).length > 0) {
            newTrip.hotelItineraries.forEach((itinerary, i) => {
              newTrip = {
                ...newTrip,
                hotelItineraries: [...newTrip.hotelItineraries],
              };
              newTrip.hotelItineraries[i] = {
                ...itinerary,
                cost: {
                  ...itinerary.cost,
                  doShowMarkupAmount: true,
                },
                hotel: {
                  ...itinerary.hotel,
                  price: {
                    ...itinerary.hotel.price,
                    doShowMarkupAmount: true,
                  },
                },
              };
            });
          } else if (mode === TravelTypes.carrental) {
            const carItinerariesKey = Object.keys(newTrip.rentalCarItineraries);
            if (carItinerariesKey.length) {
              newTrip.rentalCarItineraries = {
                ...newTrip.rentalCarItineraries,
                [carItinerariesKey[0]]: {
                  ...newTrip.rentalCarItineraries[carItinerariesKey[0]],
                  cost: {
                    ...newTrip.rentalCarItineraries[carItinerariesKey[0]].cost,
                    doShowMarkupAmount: true,
                  },
                },
              };
            }
          }
        }
      }

      return {
        ...state,
        selectedTrips: { ...state.selectedTrips, ...{ [tripId]: newTrip } },
      };
    },
    updateProductInSelectedTrip: (state, { payload: { hotelKey, product } }) => {
      const newState = { ...state };
      const tripEntry = Object.entries(newState.selectedTrips || {}).find(
        ([tripId, trip]) =>
          Array.isArray(trip?.hotelItineraries) &&
          trip.hotelItineraries.some((h) => h.hotelKey === hotelKey),
      );
      if (!tripEntry) return newState;
      const [tripId, trip] = tripEntry;

      if (!trip?.hotelItineraries) return newState;

      const updatedHotelItineraries = trip.hotelItineraries.map((itinerary) => {
        if (!itinerary.hotel?.products) return itinerary;

        const updatedProducts = itinerary.hotel.products.map((prod) => {
          if (prod.code !== product.previousProductCode) return prod;
          const newData = { ...product };
          const mergedRooms = newData.rooms
            ? newData.rooms.map((room, index) => ({
                ...room,
                images:
                  prod.rooms?.[index]?.images?.length > 0 ? prod.rooms[index].images : room.images,
                originalDesc: prod.rooms?.[index]?.originalDesc || prod.rooms?.[index]?.desc,
                originalFormattedDesc:
                  prod.rooms?.[index]?.originalFormattedDesc || prod.rooms?.[index]?.formattedDesc,
              }))
            : prod.rooms;

          return {
            ...prod,
            ...Object.fromEntries(
              Object.entries(newData).filter(([key]) => key !== 'previousProductCode'),
            ),
            rooms: mergedRooms,
            isMatchWithRequestedRatePlanCode: prod.isMatchWithRequestedRatePlanCode,
          };
        });

        const updatedItinerary = {
          ...itinerary,
          hotel: {
            ...itinerary.hotel,
            products: updatedProducts,
          },
        };

        if (updatedItinerary.selectedRoomCode === product.previousProductCode) {
          updatedItinerary.selectedRoomCode = product.code;
          updatedItinerary.selectedProduct = {
            ...updatedItinerary.selectedProduct,
            additional_info: product.additional_info,
          };
        }

        return updatedItinerary;
      });

      newState.selectedTrips = {
        ...newState.selectedTrips,
        [tripId]: {
          ...trip,
          hotelItineraries: updatedHotelItineraries,
        },
      };

      return newState;
    },
    resetError: (state) => ({
      ...state,
      error: null,
    }),
    resetSaveTripStatus: (state, { payload: { tripId } }) => ({
      ...state,
      selectedTrips: {
        ...state.selectedTrips,
        [tripId]: {
          ...state.selectedTrips[tripId],
          saveTripStatus: SearchStatus.INITIAL,
        },
      },
    }),
    resetTripStatus: (state) => ({
      ...state,
      status: SearchStatus.INITIAL,
    }),
    saveTripRequest: (state, { payload: { tripId } }) => ({
      ...state,
      error: undefined,
      getPackagesStatus: SearchStatus.INITIAL,
      getSpecialServicesStatus: SearchStatus.INITIAL,
      tripPriceCheckStatus: SearchStatus.INITIAL,
      tripBookingStatus: SearchStatus.INITIAL,
      passengers:
        state.tripConfirmation &&
        state.tripConfirmation.tripId === tripId &&
        state.tripConfirmation.travelers &&
        state.tripConfirmation.travelers.length
          ? state.passengers
          : initialState.passengers,
      selectedTrips: {
        ...state.selectedTrips,
        [tripId]: {
          ...state.selectedTrips[tripId],
          saveTripStatus: SearchStatus.SEARCHING,
        },
      },
    }),
    addCreditCard: (state) => {
      const existingSelectedCard = _.find(state.paymentMethods, (pm) => !!pm.selectedCard);
      return {
        ...state,
        paymentMethods: {
          ...state.paymentMethods,
          [Object.values(state.paymentMethods).length]: {
            ...PaymentMethodInitialState,
            selectedCard: existingSelectedCard && existingSelectedCard.selectedCard,
          },
        },
      };
    },
    removeCreditCard: (state, { payload: { cardIndex } }) => {
      if (Object.keys(state.paymentMethods).length <= 1) return state;
      const { [cardIndex]: deleted, ...remaining } = state.paymentMethods;
      return {
        ...state,
        paymentMethods: Object.fromEntries(Object.values(remaining).map((card, i) => [i, card])),
      };
    },
    saveTripSuccess: (state, action) => {
      const savedTrip = formatTrip({ trip: action.payload.trip });
      return {
        ...state,
        selectedTrips: {
          ...state.selectedTrips,
          [action.payload.tripId]: {
            ...savedTrip,
            /* Copy selection from results page to checkout page */
            itinerariesOnHold:
              state.selectedTrips[action.payload.tripId] &&
              state.selectedTrips[action.payload.tripId].itinerariesOnHold,
            saveTripStatus: SearchStatus.DONE,
          },
          [savedTrip.tripCode]: {
            ...savedTrip,
            /* Copy selection from results page to checkout page */
            itinerariesOnHold:
              state.selectedTrips[action.payload.tripId] &&
              state.selectedTrips[action.payload.tripId].itinerariesOnHold,
            saveTripStatus: SearchStatus.DONE,
            relatedTripId: action.payload.tripId,
          },
        },
      };
    },
    saveTripFailure: (state, { payload: { error, tripId } }) => ({
      ...state,
      error,
      selectedTrips: {
        ...state.selectedTrips,
        [tripId]: {
          ...state.selectedTrips[tripId],
          tripCode: null,
          saveTripStatus: SearchStatus.DONE,
        },
      },
    }),
    changeTrip: (state, { payload: { field, value } }) => ({
      ...state,
      [field]: value,
    }),
    updateTripConfirmationTravelers: (state, { payload }) => {
      if (!state.tripConfirmation) return state;

      const { travelers } = payload;
      state.tripConfirmation = {
        ...state.tripConfirmation,
        travelers,
      };
    },
    bookTripRequest: (
      state,
      { payload: { isUpdatingExistingBooking, tripConfirmationChanges } },
    ) => ({
      ...state,
      tripBookingStatus: SearchStatus.SEARCHING,
      tripConfirmation:
        isUpdatingExistingBooking || (tripConfirmationChanges && tripConfirmationChanges.hasChanges)
          ? state.tripConfirmation
          : null,
      error: null,
      bookingStatus: null,
    }),
    bookTripSuccess: (state, { payload: { data } }) => ({
      ...state,
      tripConfirmation: data && data.tripId ? data : null,
      bookingAction: data ? data.tripStatus : BookingAction.BOOK,
      tripBookingStatus: SearchStatus.DONE,
    }),
    bookTripFailure: (state, action) => {
      const savedTrip =
        action.payload.isTripAvailabilityPriceChanged &&
        action.payload.tripAvailabilityPriceChangeResponse &&
        action.payload.tripAvailabilityPriceChangeResponse.trip
          ? formatTrip({ trip: action.payload.tripAvailabilityPriceChangeResponse.trip })
          : undefined;
      const ignoreCachedResultsForModes =
        action.payload.isTripAvailabilityPriceChanged &&
        action.payload.tripAvailabilityPriceChangeResponse &&
        action.payload.tripAvailabilityPriceChangeResponse.updatedItineraries &&
        action.payload.tripAvailabilityPriceChangeResponse.updatedItineraries.length
          ? _.map(
              action.payload.tripAvailabilityPriceChangeResponse.updatedItineraries,
              (i) => i.mode,
            )
          : [];

      return {
        ...state,
        selectedTrips: !savedTrip
          ? { ...state.selectedTrips }
          : {
              ...state.selectedTrips,
              [savedTrip.tripCode]: {
                ...savedTrip,
              },
            },
        error: action.payload.error,
        tripBookingStatus: SearchStatus.DONE,
        isTripAvailabilityPriceChanged: action.payload.isTripAvailabilityPriceChanged || false,
        tripAvailabilityPriceChangeResponse: action.payload.tripAvailabilityPriceChangeResponse,
        clientOverwriteWarning: action.payload.clientOverwriteWarning || null,
        ignoreCachedResultsForModes,
      };
    },
    resetClientOverwriteConfirmation: (state) => ({
      ...state,
      clientOverwriteWarning: null,
    }),
    resetTripAvailabilityPriceStatus: (state) => ({
      ...state,
      isTripAvailabilityPriceChanged: false,
      tripAvailabilityPriceChangeResponse: null,
      acceptTripPriceChangeStatus: SearchStatus.INITIAL,
      error: null,
      priceCheckError: null,
    }),
    getTripRequest: (state) => ({
      ...state,
      status: SearchStatus.SEARCHING,
      getPackagesStatus: SearchStatus.INITIAL,
      getSpecialServicesStatus: SearchStatus.INITIAL,
      tripId: null,
    }),
    getTripSuccess: (state, action) => {
      // TODO: this is temporary fix.
      // remove it later on when update components
      // to display itinerary using new structure
      const savedTrip = formatTrip({
        trip: action.payload.trip,
        tripConfirmation: action.payload.tripConfirmation,
      });
      return {
        ...state,
        status: SearchStatus.DONE,
        selectedTrips: {
          ...state.selectedTrips,
          [savedTrip.tripCode]: {
            ...savedTrip,
          },
        },
        getPackagesStatus: SearchStatus.INITIAL,
        getSpecialServicesStatus: SearchStatus.INITIAL,
        tripPriceCheckStatus: SearchStatus.INITIAL,
      };
    },
    getTripFailure: (state, action) => ({
      ...state,
      error: action.payload,
      status: SearchStatus.DONE,
    }),

    tripPriceCheckRequest: (state) => ({
      ...state,
      tripPriceCheckStatus: SearchStatus.SEARCHING,
      tripId: null,
      walletChargeAmount: null,
    }),

    tripPriceCheckResponse: (state) => ({
      ...state,
      tripPriceCheckStatus: SearchStatus.DONE,
    }),

    getTripConfirmationRequest: (state, action) => ({
      ...state,
      status: SearchStatus.SEARCHING,
      tripId: null,
      tripConfirmation:
        action && action.payload && action.payload.doRemoveTripConfirmation
          ? null
          : state.tripConfirmation,
    }),

    getTripConfirmationSuccess: (state, action) => {
      // TODO: this is temporary fix. remove it later on
      // when update components
      // to display itinerary using new structure
      const savedTrip = formatTrip({
        trip: action.payload.tripConfirmation.trip,
        tripConfirmation: action.payload.tripConfirmation,
      });
      const { tripConfirmation } = action.payload;
      const tripBookingAction = action.payload.tripConfirmation.tripStatus;
      const isTicketed = !!tripConfirmation.bookingResponses?.some((br) => br.isTicketed === true);
      let agencyPlanningFeesKeptByAgency = 0;
      if (tripConfirmation && tripConfirmation.agencyPlanningFeesKeptByAgency > 0) {
        agencyPlanningFeesKeptByAgency = tripConfirmation.agencyPlanningFeesKeptByAgency;
      }
      const updatedTripsSearch = state.tripsSearch?.trips
        ? {
            ...state.tripsSearch,
            trips: state.tripsSearch.trips.map((trip) =>
              trip.tripId === savedTrip.id ? { ...trip, isFlightTicketed: isTicketed } : trip,
            ),
          }
        : state.tripsSearch;

      const formattedPassengers =
        formatPassengers(
          tripConfirmation,
          undefined,
          action.payload && action.payload.travelAgency,
        ) || [];

      return {
        ...state,
        tripsSearch: updatedTripsSearch,
        status: SearchStatus.DONE,
        bookingAction: tripBookingAction,
        passengers: formattedPassengers,
        selectedTrips: {
          ...state.selectedTrips,
          [savedTrip.id]: {
            ...savedTrip,
            tripCode: savedTrip.id,
            doShowAgencyPlanningFeesPerPassenger: tripConfirmation
              ? tripConfirmation.doShowAgencyPlanningFeesPerPassenger
              : savedTrip.doShowAgencyPlanningFeesPerPassenger,
          },
        },
        tripConfirmation,
        doChargeAgencyPlanningFees: (tripConfirmation || {}).agencyPlanningFees > 0,
        agencyPlanningFees: (tripConfirmation && tripConfirmation.agencyPlanningFees) || 0,
        agencyPlanningFeesKeptByAgency,
        doShowAgencyPlanningFeesPerPassenger:
          tripConfirmation && tripConfirmation.doShowAgencyPlanningFeesPerPassenger,
        doSplitServiceFees: agencyPlanningFeesKeptByAgency > 0,
        isManagingServices: false,
        isManagingInsurance: false,
        isManagingTransport: false,
        isManagingPackage: false,
        isManagingDining: false,
        getPackagesStatus: SearchStatus.INITIAL,
        getSpecialServicesStatus: SearchStatus.INITIAL,
        tripPriceCheckStatus: SearchStatus.INITIAL,
        error: Object.prototype.hasOwnProperty.call(action.payload, 'error')
          ? action.payload.error
          : state.error,
      };
    },

    setTripConfirmation: (state, action) => {
      const { tripConfirmation, isUpdatingBookedTrip, travelAgency } = action.payload;
      if (
        !tripConfirmation ||
        !tripConfirmation.travelers ||
        !Array.isArray(tripConfirmation.travelers) ||
        !tripConfirmation.travelers.length
      ) {
        return {
          ...state,
          tripConfirmation,
        };
      }

      const formattedPassengers = formatPassengers(
        tripConfirmation,
        isUpdatingBookedTrip,
        travelAgency,
      );

      return {
        ...state,
        tripConfirmation,
        passengers: formattedPassengers,
      };
    },
    getTripConfirmationFailure: (state, action) => ({
      ...state,
      error: action.payload,
      status: SearchStatus.DONE,
    }),
    removeTripConfirmation: (state) => ({
      ...state,
      status: SearchStatus.INITIAL,
      tripConfirmation: undefined,
      selectedTrips: {},
    }),
    setBookingAction: (state, action) => ({
      ...state,
      bookingAction: action.payload,
    }),
    saveSendTripEmailRequest: (state) => ({
      ...state,
      saveSendTripEmailStatus: SearchStatus.SEARCHING,
      saveSendTripEmailError: null,
    }),
    saveSendTripEmailSuccess: (state) => ({
      ...state,
      saveSendTripEmailStatus: SearchStatus.DONE,
      saveSendTripEmailError: null,
    }),
    saveSendTripEmailFailure: (state, action) => ({
      ...state,
      saveSendTripEmailError: action.payload,
      saveSendTripEmailStatus: SearchStatus.DONE,
    }),
    resetSaveSendTripEmailStatus: (state) => ({
      ...state,
      saveSendTripEmailStatus: SearchStatus.INITIAL,
      saveSendTripEmailError: null,
    }),
    getDiningOptionsRequest: (state) => ({
      ...state,
      getDiningOptionsStatus: SearchStatus.SEARCHING,
    }),
    getDiningOptionsSuccess: (state, action) => ({
      ...state,
      getDiningOptionsStatus: SearchStatus.DONE,
      selectedTrips: {
        ...state.selectedTrips,
        [action.payload.tripId]: {
          ...state.selectedTrips[action.payload.tripId],
          diningOptions: action.payload.diningOptions,
        },
      },
    }),
    getDiningOptionsFailure: (state, action) => ({
      ...state,
      error: action.payload,
      getDiningOptionsStatus: SearchStatus.DONE,
    }),
    getBasketContentRequest: (state, action) => ({
      ...state,
      getBasketContentStatuses: {
        ...state.getBasketContentStatuses,
        [action.payload.tripId]: SearchStatus.SEARCHING,
      },
    }),
    getBasketContentResponse: (state, action) => ({
      ...state,
      error: action.payload && action.payload.error,
      getBasketContentStatuses: {
        ...state.getBasketContentStatuses,
        [action.payload.tripId]: SearchStatus.DONE,
      },
    }),
    setCruiseItineraryDiningOption: (
      state,
      { payload: { tripId, selectedDiningSeat, selectedDiningTable, roomIndex } },
    ) => {
      const cruiseItinerary = {
        ...state.selectedTrips[tripId].cruiseItinerary,
        cabins: [...state.selectedTrips[tripId].cruiseItinerary.cabins],
      };

      if (selectedDiningSeat) {
        cruiseItinerary.cabins[roomIndex] = {
          ...cruiseItinerary.cabins[roomIndex],
          selectedDiningSeat,
        };
      }

      if (selectedDiningTable) {
        cruiseItinerary.cabins[roomIndex] = {
          ...cruiseItinerary.cabins[roomIndex],
          selectedDiningTable,
        };
      }

      return {
        ...state,
        getDiningOptionsStatus: SearchStatus.INITIAL,
        selectedTrips: {
          ...state.selectedTrips,
          [tripId]: {
            ...state.selectedTrips[tripId],
            cruiseItinerary,
          },
        },
      };
    },
    setCruiseItineraryDiningOptions: (state, { payload: { tripId, diningOptionsForRooms } }) => {
      const cruiseItinerary = {
        ...state.selectedTrips[tripId].cruiseItinerary,
        cabins: [...state.selectedTrips[tripId].cruiseItinerary.cabins],
      };

      diningOptionsForRooms.forEach((diningOption) => {
        if (diningOption.selectedDiningSeat) {
          cruiseItinerary.cabins[diningOption.roomIndex] = {
            ...cruiseItinerary.cabins[diningOption.roomIndex],
            selectedDiningSeat: diningOption.selectedDiningSeat,
          };
        }

        if (diningOption.selectedDiningTable) {
          cruiseItinerary.cabins[diningOption.roomIndex] = {
            ...cruiseItinerary.cabins[diningOption.roomIndex],
            selectedDiningTable: diningOption.selectedDiningTable,
          };
        }
      });

      return {
        ...state,
        getDiningOptionsStatus: SearchStatus.INITIAL,
        selectedTrips: {
          ...state.selectedTrips,
          [tripId]: {
            ...state.selectedTrips[tripId],
            cruiseItinerary,
          },
        },
      };
    },
    resendEmailRequest: (state) => ({
      ...state,
      sendClientCopyEmailError: null,
      resendEmailStatus: SearchStatus.SEARCHING,
    }),
    resendEmailSuccess: (state) => ({
      ...state,
      resendEmailStatus: SearchStatus.DONE,
    }),
    resendEmailFailure: (state, action) => ({
      ...state,
      resendEmailStatus: SearchStatus.DONE,
      ...((action && action.payload) || {}),
    }),
    sendClientCopyToClientsRequest: (state) => ({
      ...state,
      sendClientCopyToClientsError: null,
      sendClientCopyToClientsStatus: SearchStatus.SEARCHING,
    }),
    sendClientCopyToClientsSuccess: (state) => ({
      ...state,
      sendClientCopyToClientsStatus: SearchStatus.DONE,
    }),
    sendClientCopyToClientsFailure: (state, action) => ({
      ...state,
      sendClientCopyToClientsStatus: SearchStatus.DONE,
      ...((action && action.payload) || {}),
    }),
    sendAgentCopyRequest: (state) => ({
      ...state,
      sendAgentCopyEmailError: null,
      sendAgentCopyStatus: SearchStatus.SEARCHING,
    }),
    sendAgentCopySuccess: (state) => ({
      ...state,
      sendAgentCopyStatus: SearchStatus.DONE,
    }),
    sendAgentCopyFailure: (state, action) => ({
      ...state,
      sendAgentCopyStatus: SearchStatus.DONE,
      ...((action && action.payload) || {}),
    }),
    sendCancellationEmailRequest: (state, action) => ({
      ...state,
      cancellationEmailError: null,
      sendCancellationEmailStatus: {
        ...state.sendCancellationEmailStatus,
        [action.payload.tripSegmentId]: SearchStatus.SEARCHING,
      },
    }),
    sendCancellationEmailSuccess: (state, action) => ({
      ...state,
      sendCancellationEmailStatus: {
        ...state.sendCancellationEmailStatus,
        [action.payload.tripSegmentId]: SearchStatus.DONE,
      },
    }),
    sendCancellationEmailFailure: (state, action) => ({
      ...state,
      ...((action && action.payload) || {}),
      sendCancellationEmailStatus: {
        ...state.sendCancellationEmailStatus,
        [action.payload.tripSegmentId]: SearchStatus.DONE,
      },
    }),

    getRateOptionRulesRequest: (state, action) => ({
      ...state,
      rateOptionRules: {
        ...state.rateOptionRules,
        [action.payload.key]: {
          ...state.rateOptionRules[action.payload.key],
          getRateRulesStatus: SearchStatus.SEARCHING,
        },
      },
    }),

    getRateOptionRulesSuccess: (state, action) => ({
      ...state,
      rateOptionRules: {
        ...state.rateOptionRules,
        [action.payload.key]: {
          ...action.payload.rules,
          refundableStatus: action.payload.refundableStatus,
          getRateRulesStatus: SearchStatus.DONE,
        },
      },
    }),

    changeValidation: (
      state,
      {
        payload: {
          field,
          targetIndex,
          formValidations,
          similarPassengersValidation = null,
          emptyPassengerDetailValidation = null,
          isBookedTripPolicyReviewValidated = true,
          isBookingTripPolicyReviewValidated = true,
          isNoteAirDeskValidated = true,
          isSavedCardValidated = true,
          isAgencyPlanningFeesValid = true,
          isAgreedForDataCollection = true,
          isUserRemarksValidated = true,
          invalidAdditionalCheckoutFields = undefined,
          creditCardsValidations = null,
          invalidHotelSpecialRequestsBySegmentId = {},
        },
      },
    ) => {
      const commonUpdates = {
        similarPassengersValidation,
        emptyPassengerDetailValidation,
        isBookedTripPolicyReviewValidated,
        isBookingTripPolicyReviewValidated,
        isNoteAirDeskValidated,
        isSavedCardValidated,
        isAgencyPlanningFeesValid,
        isAgreedForDataCollection,
        isUserRemarksValidated,
        invalidAdditionalCheckoutFields:
          invalidAdditionalCheckoutFields !== undefined
            ? invalidAdditionalCheckoutFields
            : state.invalidAdditionalCheckoutFields,
        invalidHotelSpecialRequestsBySegmentId,
      };

      if (creditCardsValidations) {
        const updatedCreditCards = { ...state.paymentMethods };
        creditCardsValidations.forEach(({ cardIndex, cardformValidations }) => {
          if (updatedCreditCards[cardIndex]) {
            updatedCreditCards[cardIndex] = {
              ...updatedCreditCards[cardIndex],
              formValidations: {
                ...cardformValidations,
              },
            };
          }
        });
        return {
          ...state,
          paymentMethods: updatedCreditCards,
          ...commonUpdates,
        };
      }

      if (field && targetIndex > -1) {
        return {
          ...state,
          [field]: {
            ...state[field],
            [targetIndex]: {
              ...state[field][targetIndex],
              formValidations,
            },
          },
          ...commonUpdates,
        };
      }

      return {
        ...state,
        ...commonUpdates,
      };
    },

    updateTripStatusReset: (state) => ({
      ...state,
      updateTripStatus: SearchStatus.INITIAL,
    }),

    updateTripRequest: (state) => ({
      ...state,
      updateTripStatus: SearchStatus.SEARCHING,
    }),

    updateTripSuccess: (state) => ({
      ...state,
      updateTripStatus: SearchStatus.DONE,
    }),

    acceptTripPriceChangeRequest: (state) => ({
      ...state,
      priceCheckError: null,
      acceptTripPriceChangeStatus: SearchStatus.SEARCHING,
    }),

    acceptTripPriceChangeSuccess: (state, action) => ({
      ...state,
      acceptTripPriceChangeStatus: SearchStatus.DONE,
      priceCheckError: action && action.payload && action.payload.error,
    }),

    setCruiseItineraryPaymentAmount: (
      state,
      { payload: { tripId, paymentAmount, roomIndex, tripConfirmation } },
    ) => {
      let cruisePayments = [];
      let selectedTripId = tripId;
      if (tripConfirmation && tripConfirmation.tripId) {
        cruisePayments = formatCruisePayments(tripConfirmation);
        selectedTripId = tripConfirmation.tripId;
      } else if (state.selectedTrips[tripId].cruisePayments) {
        cruisePayments = [...state.selectedTrips[tripId].cruisePayments];
      }
      if (!tripConfirmation) {
        cruisePayments[roomIndex] = {
          roomIndex,
          paymentAmount,
        };
      }

      return {
        ...state,
        selectedTrips: {
          ...state.selectedTrips,
          [selectedTripId]: {
            ...state.selectedTrips[selectedTripId],
            cruisePayments,
          },
        },
        tripConfirmation: state.tripConfirmation
          ? {
              ...state.tripConfirmation,
              trip: {
                ...state.tripConfirmation.trip,
                cruisePayments,
              },
            }
          : null,
      };
    },

    searchTripsRequest: (state, { payload: { searchObj } }) => {
      const nextSearchObj = {
        ...state.tripsSearch.searchObj,
        ...searchObj,
        bookingStatus: searchObj.bookingStatus || [],
        depositDue: searchObj.depositDue || [],
        finalPaymentDue: searchObj.finalPaymentDue || [],
        fromTripBookingDate: searchObj.fromTripBookingDate || undefined,
        toTripBookingDate: searchObj.toTripBookingDate || undefined,
        tripStartDateFrom: searchObj.tripStartDateFrom || undefined,
        tripStartDateTo: searchObj.tripStartDateTo || undefined,
        customBookingFields: searchObj.customBookingFields || [],
      };

      const { areFiltersEmpty: areFiltersEmptyNext, ...nextRestSearchObj } = nextSearchObj;
      const { areFiltersEmpty: areFiltersEmptyInitialState, ...nextRestInitialState } =
        initialState.tripsSearch.searchObj;
      const nextAreFiltersEmpty = _.isEqual(nextRestSearchObj, nextRestInitialState);

      return {
        ...state,
        searchTripsStatus: SearchStatus.SEARCHING,
        tripsSearch: {
          ...state.tripsSearch,
          searchObj: {
            ...nextSearchObj,
            areFiltersEmpty: nextAreFiltersEmpty,
          },
          trips: null,
        },
      };
    },

    searchTripsReset: (state) => {
      const nextTripsSearch = {
        ...state.tripsSearch,
      };
      _.set(nextTripsSearch, 'searchObj', initialState.tripsSearch.searchObj);

      return {
        ...state,
        tripsSearch: { ...nextTripsSearch },
      };
    },

    searchTripsSuccess: (
      state,
      { payload: { allUsers, trips, totalTrips, totalPages, customFilters, affiliatedAgencies } },
    ) => ({
      ...state,
      searchTripsStatus: SearchStatus.DONE,
      tripsSearch: {
        ...state.tripsSearch,
        filterObj: {
          ...state.tripsSearch.filterObj,
          totalTrips,
          totalPages,
          allUsers,
          customFilters,
          affiliatedAgencies,
        },
        trips,
      },
    }),

    getTripsCsvRequest: (state) => ({ ...state, getTripsCsvStatus: SearchStatus.SEARCHING }),

    getTripsCsvSuccess: (state, { payload: { data } }) => ({
      ...state,
      tripsCsv: data,
      getTripsCsvStatus: SearchStatus.DONE,
    }),

    resetCancelBooking: (state) => ({
      ...state,
      cancelBookingStatus: SearchStatus.INITIAL,
      cancelBookingError: null,
      cancelBookingWasVoided: false,
    }),

    cancelBookingRequest: (state) => ({
      ...state,
      cancelBookingStatus: SearchStatus.SEARCHING,
      cancelBookingError: null,
      cancelBookingWasVoided: false,
    }),

    cancelBookingSuccess: (state, { payload: { error, errorCode, wasVoided } }) => ({
      ...state,
      cancelBookingStatus: SearchStatus.DONE,
      cancelBookingError: error,
      cancelBookingErrorCode: errorCode,
      cancelBookingWasVoided: wasVoided ?? false,
    }),

    setItineraryOnHold: (state, { payload: { tripId, itineraryMode, itineraryId } }) => {
      const itinerariesOnHold = state.selectedTrips[tripId].itinerariesOnHold
        ? [...state.selectedTrips[tripId].itinerariesOnHold]
        : [];
      const existingIndex =
        itinerariesOnHold &&
        _.findIndex(
          itinerariesOnHold,
          (m) => m.itineraryMode === itineraryMode && m.itineraryId === itineraryId,
        );
      if (existingIndex > -1) {
        itinerariesOnHold.splice(existingIndex, 1);
      } else {
        itinerariesOnHold.push({
          itineraryMode,
          itineraryId,
        });
      }
      return {
        ...state,
        selectedTrips: {
          ...state.selectedTrips,
          [tripId]: {
            ...state.selectedTrips[tripId],
            itinerariesOnHold,
          },
        },
      };
    },

    setItineraryToAddPaymentTo: (state, { payload: { tripId, itineraryMode } }) => {
      const addPaymentMethodToTravelTypes = state.selectedTrips[tripId]
        .addPaymentMethodToTravelTypes
        ? [...state.selectedTrips[tripId].addPaymentMethodToTravelTypes]
        : [];
      const existingIndex =
        addPaymentMethodToTravelTypes &&
        _.findIndex(addPaymentMethodToTravelTypes, (m) => m === itineraryMode);
      if (existingIndex > -1) {
        addPaymentMethodToTravelTypes.splice(existingIndex, 1);
      } else {
        addPaymentMethodToTravelTypes.push(itineraryMode);
      }
      return {
        ...state,
        selectedTrips: {
          ...state.selectedTrips,
          [tripId]: {
            ...state.selectedTrips[tripId],
            addPaymentMethodToTravelTypes,
          },
        },
      };
    },

    getPackagesRequest: (state) => ({
      ...state,
      getPackagesStatus: SearchStatus.SEARCHING,
    }),
    getPackagesResponse: (state, { payload: { packageOptions, error, tripId } }) => {
      if (error || !packageOptions || packageOptions.length === 0) {
        return {
          ...state,
          getPackageError: error,
          getPackagesStatus: SearchStatus.DONE,
        };
      }
      return {
        ...state,
        getPackagesStatus: SearchStatus.DONE,
        selectedTrips: {
          ...state.selectedTrips,
          [tripId]: {
            ...state.selectedTrips[tripId],
            packageOptions,
          },
        },
      };
    },

    getSpecialServicesRequest: (state) => ({
      ...state,
      getSpecialServicesStatus: SearchStatus.SEARCHING,
    }),

    getSpecialServicesResponse: (state, { payload: { specialServices, error, tripId } }) => {
      if (error || !specialServices || specialServices.length === 0) {
        return {
          ...state,
          getSpecialServicesError: error,
          getSpecialServicesStatus: SearchStatus.DONE,
        };
      }
      return {
        ...state,
        getSpecialServicesStatus: SearchStatus.DONE,
        selectedTrips: {
          ...state.selectedTrips,
          [tripId]: {
            ...state.selectedTrips[tripId],
            specialServices,
          },
        },
      };
    },

    discardTripConfirmationDiningChanges: (state) => {
      const { tripId } = state.tripConfirmation;
      const cruiseItinerary = (
        _.find(state.tripConfirmation.trip.tripSegments, (ts) => ts.mode === TravelTypes.cruise) ||
        {}
      ).itinerary;
      const trip = state.selectedTrips[tripId];
      const updatedTrip = {
        ...trip,
        cruiseItinerary: {
          ...trip.cruiseItinerary,
          cabins: [...trip.cruiseItinerary.cabins],
        },
      };
      _.each(cruiseItinerary.cabins, (c, index) => {
        updatedTrip.cruiseItinerary.cabins[index] = {
          ...updatedTrip.cruiseItinerary.cabins[index],
          selectedDiningSeat: c.selectedDiningSeat,
          selectedDiningTable: c.selectedDiningTable,
        };
      });
      return {
        ...state,
        getSpecialServicesStatus: SearchStatus.DONE,
        isManagingDining: false,
        selectedTrips: {
          ...state.selectedTrips,
          [tripId]: {
            ...state.selectedTrips[tripId],
            ...updatedTrip,
          },
        },
      };
    },

    deleteTripRequest: (state, { payload: { tripId } }) => ({
      ...state,
      deleteTripError: undefined,
      deleteTripStatus: {
        ...state.deleteTripStatus,
        [tripId]: SearchStatus.SEARCHING,
      },
    }),

    deleteTripResponse: (state, { payload: { tripId, error } }) => ({
      ...state,
      deleteTripError: error,
      deleteTripStatus: {
        ...state.deleteTripStatus,
        [tripId]: SearchStatus.DONE,
      },
    }),

    getSabrePnrPrintoutRequest: (state) => ({
      ...state,
      sabreCommandsStatus: SearchStatus.SEARCHING,
    }),

    getSabrePnrPrintoutResponse: (state, action) => ({
      ...state,
      sabreCommandsStatus: SearchStatus.DONE,
      sabrePnrPrintout: action && action.payload && action.payload.data,
    }),
    setSelfBookingAllowed: (state) => ({
      ...state,
      isSelfBookingAllowed: true,
    }),
    pollBookingRequest: (state) => ({
      ...state,
    }),
    updateFlightItinerary: (state, { payload: { tripId, flightItinerary } }) => ({
      ...state,
      selectedTrips: {
        ...state.selectedTrips,
        [tripId]: {
          ...state.selectedTrips[tripId],
          flightItinerary,
        },
      },
    }),
    removeHotelChainAdvisorSegment: (state, { payload: { tripSegmentId } }) => {
      const newAdvisorBySegment = { ...state.hotelChainAdvisorBySegment };
      const newDoSaveBySegment = { ...state.doSaveHotelChainAdvisorBySegment };
      delete newAdvisorBySegment[tripSegmentId];
      delete newDoSaveBySegment[tripSegmentId];
      return {
        ...state,
        hotelChainAdvisorBySegment: newAdvisorBySegment,
        doSaveHotelChainAdvisorBySegment: newDoSaveBySegment,
      };
    },
    initHotelChainAdvisorSegment: (
      state,
      { payload: { tripSegmentId, hotelChainCode, advisorId, hotelChainName } },
    ) => ({
      ...state,
      hotelChainAdvisorBySegment: state.hotelChainAdvisorBySegment?.[tripSegmentId]
        ? state.hotelChainAdvisorBySegment
        : {
            ...state.hotelChainAdvisorBySegment,
            [tripSegmentId]: { hotelChainCode, advisorId, hotelChainName },
          },
      doSaveHotelChainAdvisorBySegment:
        state.doSaveHotelChainAdvisorBySegment?.[tripSegmentId] !== undefined
          ? state.doSaveHotelChainAdvisorBySegment
          : {
              ...state.doSaveHotelChainAdvisorBySegment,
              [tripSegmentId]: false,
            },
    }),
    updateInsuranceItinerary: (state, { payload: { tripId, insuranceItinerary } }) => {
      if (!state.selectedTrips[tripId]) {
        return state;
      }

      return {
        ...state,
        selectedTrips: {
          ...state.selectedTrips,
          [tripId]: {
            ...state.selectedTrips[tripId],
            insuranceItinerary,
          },
        },
      };
    },
    setSelectedCreditCard: (
      state,
      { payload: { customerId, paymentMethodIndex, selectedCard } },
    ) => {
      var stateToUpdate = {
        ...state,
        paymentMethods: {
          ...state.paymentMethods,
        },
      };

      if (paymentMethodIndex >= -1) {
        // If paymentMethodIndex provided then update specific card
        stateToUpdate.paymentMethods[paymentMethodIndex] = {
          ...stateToUpdate.paymentMethods[paymentMethodIndex],
          selectedCard: {
            ...(selectedCard || {}),
            customerId: selectedCard ? customerId : undefined,
          },
        };
      } else {
        // If paymentMethodIndex not provided, then update selected card with all payment methods
        Object.keys(stateToUpdate.paymentMethods).forEach((key) => {
          stateToUpdate.paymentMethods[key] = {
            ...stateToUpdate.paymentMethods[key],
            selectedCard: {
              ...(selectedCard || {}),
              customerId: selectedCard ? customerId : undefined,
            },
          };
        });
      }
      return stateToUpdate;
    },
    setMarkup: (state, action) => {
      const { tripId, tripSegmentId, value } = action.payload;
      if (!state.markups[tripId]) {
        state.markups[tripId] = {};
      }
      state.markups[tripId][tripSegmentId] = value;
    },

    addCustomBookingFieldRequest: (state) => ({
      ...state,
      addCustomBookingFieldStatus: SearchStatus.SEARCHING,
      addCustomBookingFieldError: null,
    }),

    addCustomBookingFieldSuccess: (state, { payload: { tripConfirmation } }) => {
      const savedTrip = formatTrip({
        trip: tripConfirmation.trip,
        tripConfirmation,
      });
      return {
        ...state,
        addCustomBookingFieldStatus: SearchStatus.DONE,
        addCustomBookingFieldError: null,
        tripConfirmation,
        selectedTrips: {
          ...state.selectedTrips,
          [savedTrip.id]: {
            ...state.selectedTrips[savedTrip.id],
            ...savedTrip,
            tripCode: savedTrip.id,
          },
        },
      };
    },

    addCustomBookingFieldFailure: (state, { payload }) => ({
      ...state,
      addCustomBookingFieldStatus: SearchStatus.DONE,
      addCustomBookingFieldError: payload,
    }),

    resetAddCustomBookingField: (state) => ({
      ...state,
      addCustomBookingFieldStatus: SearchStatus.INITIAL,
      addCustomBookingFieldError: null,
    }),
  },
});

export const setDefaultSelectedCard = ({ cards, customerId, paymentMethodIndex }) => {
  const selectedCard =
    cards && cards.length
      ? _.find(cards, (c) => c.isDefault && !c.isExpired) ||
        _.find(cards, (c) => !c.isExpired) ||
        cards[0]
      : undefined;

  return setSelectedCreditCard({ customerId, paymentMethodIndex, selectedCard });
};

export const getCustomerIdToSaveCard = (state) => {
  if (
    state &&
    state.user &&
    state.user.travelAgency &&
    state.user.travelAgency.useCardFromAgencyProfile
  ) {
    return '';
  }
  return (
    (state &&
      state.trip &&
      state.trip.passengers &&
      state.trip.passengers[0] &&
      state.trip.passengers[0].customerId) ||
    ''
  );
};

export const selectedCreditCard = (paymentMethodIndex) => (state) => {
  return (
    state.trip.paymentMethods &&
    state.trip.paymentMethods[paymentMethodIndex] &&
    state.trip.paymentMethods[paymentMethodIndex].selectedCard &&
    (state.trip.paymentMethods[paymentMethodIndex].selectedCard.customerId || '') ===
      (getCustomerIdToSaveCard(state) || '') &&
    state.trip.paymentMethods[paymentMethodIndex].selectedCard
  );
};

export const selectInsuranceItinerary =
  ({ tripId }) =>
  (state) => {
    const insuranceItinerary = state.trip.selectedTrips[tripId]?.insuranceItinerary;
    return insuranceItinerary;
  };

export const {
  addToTrip,
  updateProductInSelectedTrip,
  removeFromTrip,
  addCreditCard,
  resetError,
  resetSaveTripStatus,
  resetTripStatus,
  saveTripRequest,
  saveTripSuccess,
  saveTripFailure,
  changeTrip,
  updateTripConfirmationTravelers,
  bookTripRequest,
  bookTripSuccess,
  bookTripFailure,
  resetTripAvailabilityPriceStatus,
  resetClientOverwriteConfirmation,
  getTripRequest,
  getTripSuccess,
  getTripFailure,
  getTripConfirmationRequest,
  getTripConfirmationSuccess,
  setTripConfirmation,
  getTripConfirmationFailure,
  removeTripConfirmation,
  setBookingAction,
  saveSendTripEmailRequest,
  saveSendTripEmailSuccess,
  saveSendTripEmailFailure,
  resetSaveSendTripEmailStatus,
  getDiningOptionsRequest,
  getDiningOptionsSuccess,
  getDiningOptionsFailure,
  setCruiseItineraryDiningOption,
  setCruiseItineraryDiningOptions,
  resendEmailRequest,
  resendEmailSuccess,
  resendEmailFailure,
  sendClientCopyToClientsRequest,
  sendClientCopyToClientsSuccess,
  sendClientCopyToClientsFailure,
  sendAgentCopyRequest,
  sendAgentCopySuccess,
  sendAgentCopyFailure,
  sendCancellationEmailRequest,
  sendCancellationEmailSuccess,
  sendCancellationEmailFailure,
  getRateOptionRulesRequest,
  getRateOptionRulesSuccess,
  changeValidation,
  updateTripRequest,
  updateTripSuccess,
  updateTripStatusReset,
  tripPriceCheckRequest,
  tripPriceCheckResponse,
  acceptTripPriceChangeRequest,
  acceptTripPriceChangeSuccess,
  setCruiseItineraryPaymentAmount,
  resetTrips,
  searchTripsRequest,
  searchTripsReset,
  searchTripsSuccess,
  cancelBookingRequest,
  cancelBookingSuccess,
  getTripsCsvRequest,
  getTripsCsvSuccess,
  resetCancelBooking,
  setItineraryOnHold,
  setItineraryToAddPaymentTo,
  getPackagesRequest,
  getPackagesResponse,
  getSpecialServicesRequest,
  getSpecialServicesResponse,
  discardTripConfirmationDiningChanges,
  deleteTripRequest,
  deleteTripResponse,
  getSabrePnrPrintoutRequest,
  getSabrePnrPrintoutResponse,
  getBasketContentRequest,
  getBasketContentResponse,
  setSelfBookingAllowed,
  removeCreditCard,
  pollBookingRequest,
  updateFlightItinerary,
  updateInsuranceItinerary,
  removeHotelChainAdvisorSegment,
  initHotelChainAdvisorSegment,
  setSelectedCreditCard,
  setMarkup,
  addCustomBookingFieldRequest,
  addCustomBookingFieldSuccess,
  addCustomBookingFieldFailure,
  resetAddCustomBookingField,
} = tripSlice.actions;
export default tripSlice.reducer;
